/**
 * Tiny decoupled event bridge for the "email not verified" fallback.
 *
 * The Apollo client is created outside the React tree (src/index.js), so it
 * can't open a React dialog directly. When a gated mutation slips past the
 * proactive UI checks and the server returns an EMAIL_NOT_VERIFIED error, the
 * Apollo error link emits this event and the always-mounted
 * UnverifiedEmailBanner listens for it to open the verify dialog.
 *
 * Using a window CustomEvent keeps the link layer free of React imports and
 * avoids a global singleton/store just for this one signal.
 */
export const EMAIL_NOT_VERIFIED_EVENT = "p2pcarsales:email-not-verified";

/**
 * Signal that the user just verified their email through a flow that lives
 * outside the banner (e.g. the magic-link landing page). The always-mounted
 * banner listens and refetches the profile so its cached `emailVerified` flag
 * and any gated CTAs update without a page reload.
 */
export const EMAIL_VERIFIED_EVENT = "p2pcarsales:email-verified";

/** Fire-and-forget signal that a gated action was blocked server-side. */
export function emitEmailNotVerified(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EMAIL_NOT_VERIFIED_EVENT));
}

/**
 * Subscribe to the EMAIL_NOT_VERIFIED signal. Returns an unsubscribe function
 * suitable for returning directly from a React useEffect.
 */
export function onEmailNotVerified(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = () => handler();
  window.addEventListener(EMAIL_NOT_VERIFIED_EVENT, listener);
  return () => window.removeEventListener(EMAIL_NOT_VERIFIED_EVENT, listener);
}

/** Fire-and-forget signal that the user just became verified. */
export function emitEmailVerified(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EMAIL_VERIFIED_EVENT));
}

/** Subscribe to the EMAIL_VERIFIED signal; returns an unsubscribe function. */
export function onEmailVerified(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = () => handler();
  window.addEventListener(EMAIL_VERIFIED_EVENT, listener);
  return () => window.removeEventListener(EMAIL_VERIFIED_EVENT, listener);
}

/**
 * True when a GraphQL/network error payload carries the EMAIL_NOT_VERIFIED
 * code. Handles both the GraphQL-errors array and the Express middleware's
 * 403 body (surfaced by Apollo as networkError.result.errors).
 */
export function hasEmailNotVerifiedCode(errors: unknown): boolean {
  if (!Array.isArray(errors)) return false;
  return errors.some(
    (e) =>
      e &&
      typeof e === "object" &&
      (e as { extensions?: { code?: string } }).extensions?.code ===
        "EMAIL_NOT_VERIFIED"
  );
}
